import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
} from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertCircle, CheckCircle2, Loader2 } from "lucide-react";
import { validatePasswordStrength } from "@/utils/passwordValidator";
import { handleError } from "@/lib/errorHandling";
import {
  verifyWelcomeToken,
  completeWelcomeSetup,
} from "@/services/authService";
import { useAuth } from "@/contexts/AuthContext";
import type { UserData } from "@/services/tokenService";

function extractApiErrorMessage(resp: {
  message?: string;
  error?: string | { message?: string; code?: string };
}): string | null {
  if (resp.error && typeof resp.error === "object" && resp.error.message) {
    return resp.error.message;
  }
  if (typeof resp.message === "string" && resp.message.trim()) {
    return resp.message;
  }
  if (typeof resp.error === "string" && resp.error !== "VALIDATION_ERROR") {
    return resp.error;
  }
  return null;
}

export default function SetPassword() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [searchParams] = useSearchParams();
  const token = useMemo(() => searchParams.get("token") || "", [searchParams]);

  const [verifying, setVerifying] = useState(true);
  const [tokenValid, setTokenValid] = useState<boolean | null>(null);
  const [verifyError, setVerifyError] = useState<string | null>(null);
  const [alreadyHasPassword, setAlreadyHasPassword] = useState(false);
  const [userName, setUserName] = useState<string | null>(null);

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    async function doVerify() {
      if (!token) {
        setVerifying(false);
        setTokenValid(false);
        setVerifyError("Link inválido: token ausente.");
        return;
      }

      try {
        const resp = await verifyWelcomeToken(token);
        if (resp.success && resp.data) {
          setTokenValid(true);
          setVerifyError(null);
          setUserName(resp.data.name || null);
          setAlreadyHasPassword(Boolean(resp.data.password_already_set));
        } else {
          setTokenValid(false);
          const code = resp.error?.code;
          if (code === "TOKEN_EXPIRED") {
            setVerifyError(
              "Este link expirou (validade de 72 horas). Peça ao administrador da Eteg para reenviar o e-mail de boas-vindas.",
            );
          } else {
            setVerifyError(
              resp.error?.message ||
                "Link inválido ou já utilizado. Solicite um novo e-mail ao administrador.",
            );
          }
        }
      } catch (err) {
        const info = handleError(err);
        setTokenValid(false);
        setVerifyError(info.message || "Falha ao validar o link.");
      } finally {
        setVerifying(false);
      }
    }
    doVerify();
  }, [token]);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitError(null);

    const passValidation = validatePasswordStrength(password);
    if (!passValidation.isValid) {
      setSubmitError(passValidation.errors.join(". "));
      return;
    }

    if (password !== confirmPassword) {
      setSubmitError("As senhas não coincidem");
      return;
    }

    setSubmitting(true);
    try {
      const resp = await completeWelcomeSetup(token, password);
      if (!resp.success || !resp.data?.token || !resp.data?.user) {
        const code =
          typeof resp.error === "object" ? resp.error?.code : undefined;
        if (code === "TOKEN_EXPIRED") {
          setSubmitError(
            "Este link expirou (validade de 72 horas). Peça ao administrador para reenviar o e-mail de boas-vindas.",
          );
        } else {
          setSubmitError(
            extractApiErrorMessage(resp) ||
              "Não foi possível criar a senha. Verifique os requisitos e tente novamente.",
          );
        }
        return;
      }

      login(resp.data.token, resp.data.user as unknown as UserData);
      navigate("/", { replace: true });
    } catch (err) {
      const info = handleError(err);
      setSubmitError(info.message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <Card className="max-w-md w-full">
        <CardHeader>
          <CardTitle>Criar senha</CardTitle>
          <CardDescription>
            {verifying
              ? "Verificando seu e-mail..."
              : alreadyHasPassword
                ? "Seu e-mail já foi verificado. Faça login com a senha cadastrada."
                : tokenValid
                  ? userName
                    ? `Olá, ${userName}. Defina sua senha para ativar a conta.`
                    : "Defina sua senha para ativar a conta."
                  : "Não foi possível usar este link."}
          </CardDescription>
        </CardHeader>

        <CardContent>
          {verifying && (
            <div className="flex justify-center py-8">
              <Loader2 className="h-10 w-10 animate-spin text-primary" />
            </div>
          )}

          {!verifying && tokenValid && !alreadyHasPassword && (
            <form onSubmit={onSubmit} className="space-y-4">
              <Alert>
                <CheckCircle2 className="h-4 w-4" />
                <AlertDescription>
                  E-mail verificado. Crie sua senha para acessar a plataforma.
                </AlertDescription>
              </Alert>

              {submitError && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{submitError}</AlertDescription>
                </Alert>
              )}

              <div className="space-y-2">
                <Label htmlFor="new-password">Senha</Label>
                <Input
                  id="new-password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Digite sua senha"
                  required
                  disabled={submitting}
                  autoComplete="new-password"
                />
                <p className="text-xs text-muted-foreground">
                  Mínimo 8 caracteres, com letra maiúscula, minúscula, número e
                  caractere especial (!@#$%…).
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirm-password">Confirmar senha</Label>
                <Input
                  id="confirm-password"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Repita a senha"
                  required
                  disabled={submitting}
                  autoComplete="new-password"
                />
              </div>

              <CardFooter className="px-0 flex gap-2 justify-end">
                <Button type="submit" disabled={submitting}>
                  {submitting ? "Salvando..." : "Salvar e entrar"}
                </Button>
              </CardFooter>
            </form>
          )}

          {!verifying && tokenValid && alreadyHasPassword && (
            <div className="space-y-4">
              <Alert>
                <CheckCircle2 className="h-4 w-4" />
                <AlertDescription>
                  Conta pronta. Você já pode fazer login.
                </AlertDescription>
              </Alert>
              <Button asChild>
                <Link to="/">Ir para o login</Link>
              </Button>
            </div>
          )}

          {!verifying && tokenValid === false && (
            <div className="space-y-4">
              {verifyError && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{verifyError}</AlertDescription>
                </Alert>
              )}
              <Button asChild>
                <Link to="/">Voltar ao início</Link>
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
